/** Master type keys for the Transport service in the Masters tab */

import type { MasterFieldDef } from './masters/types';

export const VEHICLE_FLEET_MASTERS = [
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
] as const;

const RIDE_CATALOG = [
  'trip_types',
  ...VEHICLE_FLEET_MASTERS,
] as const;

/** Transport master types that auto-generate code from display name (create flow). */
export const TRANSPORT_AUTO_CODE_TYPES = new Set<string>([
  'trip_types',
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
  'ride_attributes',
  'ride_attribute_values',
  'ride_tags',
  'service_availability_schedules',
]);

/** Transport master types whose display name allows letters and spaces only. */
export const TRANSPORT_LETTER_ONLY_NAME_TYPES = new Set<string>([
  'trip_types',
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
  'ride_attributes',
  'ride_attribute_values',
  'ride_tags',
  'service_availability_schedules',
]);

/** Transport master types with description spam validation (repeated chars/words). */
export const TRANSPORT_DESCRIPTION_VALIDATED_TYPES = new Set<string>([
  'trip_types',
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
]);

/** Transport master types that hide the Active toggle in add/edit form. */
export const TRANSPORT_HIDE_STATUS_TOGGLE_TYPES = new Set<string>([
  'trip_types',
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
  'ride_attributes',
  'ride_attribute_values',
  'ride_tags',
  'service_availability_schedules',
]);

/** Transport master types that hide the record-level Description field in add/edit form. */
export const TRANSPORT_HIDE_DESCRIPTION_TYPES = new Set<string>([
  'ride_attributes',
  'ride_attribute_values',
  'ride_tags',
  'service_availability_schedules',
]);

/** Transport master types deleted permanently from the list (not soft-deactivated). */
export const TRANSPORT_HARD_DELETE_TYPES = new Set<string>([
  'trip_types',
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
  'ride_attributes',
  'ride_attribute_values',
  'ride_tags',
  'service_availability_schedules',
]);

/** Transport master types that show Active/Inactive toggle in the list table. */
export const TRANSPORT_STATUS_TOGGLE_TYPES = new Set<string>([
  'trip_types',
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
  'ride_attributes',
  'ride_attribute_values',
  'ride_tags',
  'service_availability_schedules',
]);

export function resolveMasterRecordActive(row: {
  isActive?: boolean;
  metadata?: Record<string, unknown>;
}): boolean {
  if (row.isActive === false) return false;
  const metaStatus = String(row.metadata?.status ?? '').trim().toLowerCase();
  if (metaStatus === 'inactive') return false;
  if (metaStatus === 'active') return true;
  return true;
}

export function supportsTransportMasterStatusToggle(masterType: string): boolean {
  return TRANSPORT_STATUS_TOGGLE_TYPES.has(masterType);
}

export function supportsTransportHardDelete(masterType: string): boolean {
  return TRANSPORT_HARD_DELETE_TYPES.has(masterType);
}

export function supportsTransportHiddenDescription(masterType: string): boolean {
  return TRANSPORT_HIDE_DESCRIPTION_TYPES.has(masterType);
}

/** Core trip type codes that cannot be permanently deleted. */
export const PROTECTED_TRIP_TYPE_CODES = new Set<string>([
  'LOCAL',
  'RENTAL',
  'OUTSTATION',
]);

export function canDeleteTripTypeMaster(row: { code?: string }): boolean {
  return !PROTECTED_TRIP_TYPE_CODES.has(String(row.code || '').toUpperCase());
}

/** Transport master types whose sort order must follow strict 1..n sequence on create. */
export const TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES = new Set<string>([
  'trip_types',
]);

/** Transport master types whose sort order must be a whole number (no decimals). */
export const TRANSPORT_INTEGER_SORT_ORDER_TYPES = new Set<string>([
  'trip_types',
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
  'ride_attributes',
  'ride_attribute_values',
  'ride_tags',
  'service_availability_schedules',
]);

/** Transport master types whose sort order must be unique across records. */
export const TRANSPORT_UNIQUE_SORT_ORDER_TYPES = new Set<string>([
  'vehicle_types',
  'vehicle_brands',
  'vehicle_models',
  'vehicle_variants',
  'ride_attributes',
  'ride_tags',
  'service_availability_schedules',
]);

/** Sort order unique within the same parent ride attribute (not globally). */
export const TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES = new Set<string>([
  'ride_attribute_values',
]);

export type SortOrderDuplicateRecord = {
  _id?: string;
  sortOrder?: number;
  parentId?: string;
  attributeCode?: string;
};

export function filterRecordsForParentScopedSortOrder(
  masterType: string,
  records: SortOrderDuplicateRecord[],
  scope: { parentId?: string; attributeCode?: string },
): SortOrderDuplicateRecord[] {
  if (!TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(masterType)) return records;
  const parentId = scope.parentId?.trim();
  const attributeCode = scope.attributeCode?.trim().toUpperCase();
  if (!parentId && !attributeCode) return [];
  return records.filter((row) => {
    if (parentId && row.parentId === parentId) return true;
    if (attributeCode && String(row.attributeCode || '').trim().toUpperCase() === attributeCode) return true;
    return false;
  });
}

export function enrichRideAttributeValueMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields.filter((field) => (
    field.key !== 'status'
    && field.key !== 'sortOrder'
    && field.key !== 'description'
    && field.key !== 'inputType'
    && field.key !== 'attributeCode'
  ));
}

export const RIDE_TAG_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

export function isRideTagColorField(masterTypeKey: string, fieldKey: string): boolean {
  return masterTypeKey === 'ride_tags' && fieldKey === 'color';
}

export function sanitizeRideTagColorInput(raw: string): string | null {
  let value = raw.trim();
  if (!value) return '';
  if (!value.startsWith('#')) value = `#${value}`;
  const hex = value.slice(1);
  if (!/^[0-9A-Fa-f]*$/.test(hex)) return null;
  if (hex.length > 6) return null;
  return `#${hex}`;
}

export function validateRideTagColor(
  value: string | number | boolean | undefined,
  label = 'Tag Color',
  opts?: { required?: boolean },
): string | undefined {
  const raw = value === undefined || value === null ? '' : String(value).trim();
  if (!raw) {
    return opts?.required ? `${label} is required` : undefined;
  }
  if (!RIDE_TAG_COLOR_PATTERN.test(raw)) {
    return `${label} must be a valid hex code (e.g. #f59e0b)`;
  }
  return undefined;
}

export function enrichRideTagMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
      && field.key !== 'description'
      && field.key !== 'inputType'
    ))
    .map((field) => (
      field.key === 'color'
        ? {
          ...field,
          type: 'text' as const,
          placeholder: '#f59e0b',
          helperText: 'Use hex format — e.g. #f59e0b',
        }
        : field
    ));
}

export function enrichServiceAvailabilityScheduleMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => (
      field.key !== 'status'
      && field.key !== 'sortOrder'
      && field.key !== 'description'
      && field.key !== 'inputType'
      && field.key !== 'day'
    ))
    .map((field) => {
      if (field.key === 'serviceSlug') {
        return {
          ...field,
          type: 'select' as const,
          required: true,
          helperText: 'Which Transport sub-service this schedule applies to — e.g. local taxi, outstation, or rental',
          options: TRANSPORT_AVAILABILITY_SERVICE_SLUG_OPTIONS.map((option) => ({ ...option })),
        };
      }
      if (field.key === 'daysOfWeek') {
        return {
          ...field,
          label: 'Days',
          type: 'select' as const,
          multiple: true,
          required: true,
          helperText: 'Select one or more days this availability window applies to',
          options: TRANSPORT_DAY_OF_WEEK_OPTIONS.map((option) => ({ ...option })),
        };
      }
      if (field.key === 'startTime' || field.key === 'endTime') {
        return {
          ...field,
          type: 'time' as const,
          required: true,
          helperText: '24-hour format HH:mm — use the time picker',
          pattern: '^([01]\\d|2[0-3]):[0-5]\\d$',
          patternMessage: 'Use 24-hour format HH:mm (e.g. 06:00, 22:30)',
        };
      }
      return field;
    });
}

/** Next sort order when adding the (existingCount + 1)th record — e.g. 6 records → 7. */
export function getNextSequentialSortOrder(existingCount: number): number {
  return Math.max(1, existingCount + 1);
}

export function validateIntegerSortOrder(value: number, label = 'Sort order'): string | undefined {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    return `${label} must be a whole number (no decimals)`;
  }
  if (value < 1) {
    return `${label} must be at least 1`;
  }
  if (value > 9999) {
    return `${label} must be 9999 or less`;
  }
  return undefined;
}

export const PASSENGER_CAPACITY_MIN = 1;
export const PASSENGER_CAPACITY_MAX = 99;

export function isVehiclePassengerCapacityField(masterTypeKey: string, fieldKey: string): boolean {
  return masterTypeKey === 'vehicle_types' && fieldKey === 'capacity';
}

/** Returns parsed capacity, empty string, or null when input must be rejected. */
export function sanitizePassengerCapacityInput(raw: string): number | '' | null {
  if (raw === '') return '';
  if (/[^0-9]/.test(raw)) return null;
  const num = Number(raw);
  if (!Number.isInteger(num) || num < PASSENGER_CAPACITY_MIN || num > PASSENGER_CAPACITY_MAX) return null;
  return num;
}

export function validatePassengerCapacity(
  value: string | number | boolean | undefined,
  label = 'Passenger Capacity',
  opts?: { required?: boolean },
): string | undefined {
  if (value === undefined || value === null || value === '') {
    return opts?.required ? `${label} is required` : undefined;
  }
  if (typeof value === 'boolean') {
    return `${label} must be a valid number`;
  }
  if (typeof value === 'number' && !Number.isInteger(value)) {
    return `${label} must be a whole number (no decimals)`;
  }
  if (typeof value === 'number' && value < 0) {
    return `${label} must be greater than 0`;
  }
  const raw = String(value).trim();
  if (raw.startsWith('-') || raw.startsWith('+')) {
    return `${label} must be greater than 0`;
  }
  if (raw.includes('.') || raw.includes(',')) {
    return `${label} must be a whole number (no decimals)`;
  }
  if (!/^\d+$/.test(raw)) {
    return `${label} must be a valid whole number`;
  }
  const num = Number(raw);
  if (!Number.isFinite(num) || !Number.isInteger(num)) {
    return `${label} must be a valid whole number`;
  }
  if (num < PASSENGER_CAPACITY_MIN) {
    return `${label} must be greater than 0`;
  }
  if (num > PASSENGER_CAPACITY_MAX) {
    return `${label} must be ${PASSENGER_CAPACITY_MAX} or less`;
  }
  return undefined;
}

export function enrichTransportVehicleMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return fields
    .filter((field) => field.key !== 'status' && field.key !== 'sortOrder')
    .map((field) => {
      if (field.key === 'capacity') {
        return {
          ...field,
          type: 'number' as const,
          required: true,
          min: PASSENGER_CAPACITY_MIN,
          max: PASSENGER_CAPACITY_MAX,
        };
      }
      if (field.key === 'serviceTypes') {
        return {
          ...field,
          type: 'master' as const,
          masterType: 'trip_types',
          multiple: true,
          required: true,
          helperText: 'Select one or more trip types this vehicle supports',
        };
      }
      return field;
    });
}

/** Known metadata keys used for fleet master images across schema versions. */
export const TRANSPORT_FLEET_IMAGE_META_KEYS = [
  'imageUrl',
  'image',
  'icon',
  'iconUrl',
  'highlightImageUrl',
] as const;

export function resolveTransportMasterImageUrl(metadata?: Record<string, unknown>): string {
  if (!metadata) return '';
  for (const key of TRANSPORT_FLEET_IMAGE_META_KEYS) {
    const raw = metadata[key];
    if (raw != null && String(raw).trim()) return String(raw).trim();
  }
  return '';
}

export function enrichTransportBrandMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return enrichTransportFleetImageMetadataFields(fields);
}

export function enrichTransportModelMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return enrichTransportFleetImageMetadataFields(fields);
}

export function enrichTransportVariantMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  return enrichTransportFleetImageMetadataFields(fields);
}

export function enrichTransportFleetImageMetadataFields(fields: MasterFieldDef[]): MasterFieldDef[] {
  const cleaned = fields.filter((field) => field.key !== 'status' && field.key !== 'description');
  const hasImage = cleaned.some((field) => field.type === 'image' || TRANSPORT_FLEET_IMAGE_META_KEYS.includes(field.key as typeof TRANSPORT_FLEET_IMAGE_META_KEYS[number]));
  if (hasImage) return cleaned;
  return [
    { key: 'imageUrl', label: 'Image', type: 'image' as const, helperText: 'Upload image — stored locally or on S3 based on tenant storage settings' },
    ...cleaned,
  ];
}

export function parseCommaSeparatedMasterCodes(value: unknown): string[] {
  return String(value ?? '')
    .split(',')
    .map((part) => part.trim().toUpperCase())
    .filter(Boolean);
}

export function formatCommaSeparatedMasterCodes(codes: string[]): string {
  const normalized = [...new Set(codes.map((code) => code.trim().toUpperCase()).filter(Boolean))];
  return normalized.join(',');
}

export function validateVehicleServiceTypes(
  value: string | number | boolean | undefined,
  label = 'Trip / Service Types',
): string | undefined {
  const codes = parseCommaSeparatedMasterCodes(value);
  if (!codes.length) return `${label} is required — select at least one trip type`;
  return undefined;
}

const SLUG_MASTER_TYPES: Record<string, readonly string[]> = {
  transport: RIDE_CATALOG,
};

export function getTransportMasterTypesForSlug(slug?: string): string[] {
  if (!slug) return [...RIDE_CATALOG];
  return [...(SLUG_MASTER_TYPES[slug] || RIDE_CATALOG)];
}

export function transportHubPoiTypesForSlug(_slug?: string): string[] {
  return [];
}

export function isRentalSlug(slug?: string): boolean {
  return slug === 'car-rental' || slug === 'bike-rental' || slug === 'rental';
}

export function isOutstationSlug(slug?: string): boolean {
  return slug === 'outstation' || slug === 'intercity';
}

export function isCorporateSlug(_slug?: string): boolean {
  return false;
}

export function isCourierSlug(_slug?: string): boolean {
  return false;
}

export function isPorterSlug(_slug?: string): boolean {
  return false;
}

export function transportSlugUsesVehicleFleet(serviceSlug?: string): boolean {
  return !serviceSlug || serviceSlug === 'transport';
}

export function shouldShowTransportHubsGroup(_slug?: string): boolean {
  return false;
}

export function isRentalSubService(slug?: string): boolean {
  return isRentalSlug(slug);
}

export function isOutstationSubService(slug?: string): boolean {
  return isOutstationSlug(slug);
}

export const DYNAMIC_TRANSPORT_FLOW = {
  title: 'Transport masters',
  description: 'Configure vehicle fleet, trip types, ride attributes, and availability directly under Transport — no sub-services.',
  steps: 'Trip types → Vehicle fleet → Attributes → Availability → Zone pricing',
};

export const TRANSPORT_SERVICE_MASTERS_INFO = {
  title: 'Transport service masters',
  description: 'Masters listed below apply to Transport. Fare rules and zone commission are set in Zone Management.',
};

export const VEHICLE_FLEET_INFO = {
  title: 'Vehicle fleet hierarchy',
  description: 'Vehicle Type → Brand → Model → Variant. Used when onboarding drivers and calculating zone fares.',
  order: 'vehicle_types → vehicle_brands → vehicle_models → vehicle_variants',
};

export const SERVICE_AVAILABILITY_INFO = {
  title: 'Availability schedules',
  description: 'Define when each Transport sub-service accepts bookings by day and time window.',
  flow: 'Pick sub-service (Transport, Outstation, Rental, etc.) → Set day/time window → Runtime checks this before allowing bookings',
};

/** Sub-service slugs used by service_availability_schedules.metadata.serviceSlug */
export const TRANSPORT_AVAILABILITY_SERVICE_SLUG_OPTIONS = [
  { value: 'transport', label: 'Transport (Local Taxi)' },
  { value: 'outstation', label: 'Outstation' },
  { value: 'intercity', label: 'Intercity' },
  { value: 'car-rental', label: 'Car Rental' },
  { value: 'bike-rental', label: 'Bike Rental' },
] as const;

export const TRANSPORT_DAY_OF_WEEK_OPTIONS = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
] as const;

export function parseCommaSeparatedDayValues(value: string | number | boolean | undefined): string[] {
  return String(value ?? '')
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

export function formatCommaSeparatedDayValues(days: string[]): string {
  const normalized = [...new Set(days.map((day) => day.trim().toLowerCase()).filter(Boolean))];
  return normalized.join(',');
}

export function formatAvailabilityDaysOfWeek(value: unknown): string {
  const codes = parseCommaSeparatedDayValues(
    typeof value === 'string' || typeof value === 'number' ? value : '',
  );
  if (!codes.length) return '—';
  const labelByValue = new Map<string, string>(TRANSPORT_DAY_OF_WEEK_OPTIONS.map((option) => [option.value, option.label]));
  return codes.map((code) => labelByValue.get(code) || code).join(', ');
}

export function normalizeServiceAvailabilityDaysMetadata(
  metadata: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> {
  const daysOfWeek = String(metadata.daysOfWeek ?? '').trim();
  const day = String(metadata.day ?? '').trim().toLowerCase();
  if (!daysOfWeek && day) {
    return { ...metadata, daysOfWeek: day };
  }
  return metadata;
}

export function syncServiceAvailabilityDaysForSave(
  metadata: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> {
  const days = parseCommaSeparatedDayValues(metadata.daysOfWeek);
  const daysOfWeek = formatCommaSeparatedDayValues(days);
  return {
    ...metadata,
    daysOfWeek,
    day: days[0] || String(metadata.day || '').trim().toLowerCase(),
  };
}

export const RENTAL_MASTERS_INFO = {
  title: 'Rental packages',
  description: 'Hour/km rental packages per zone — use Rental Master tab or Operations → Rental Management.',
  flow: 'Select zone → Add package → Per-vehicle pricing grid',
  examples: '4hr/40km Sedan ₹899 · grace 15min · extra km ₹12',
};

export const OUTSTATION_MASTERS_INFO = {
  title: 'Outstation pricing',
  description: 'Zone + vehicle + trip type pricing — the only outstation fare master.',
  flow: 'Select zone → Add pricing per vehicle → Separate rows for One Way and Round Trip',
  examples: 'Sedan One Way: base ₹500 + ₹14/km · Round Trip: base ₹800 + ₹12/km · bata ₹300/day',
};

export const TRANSPORT_HUBS_INFO = {
  title: 'Transport hubs (POI)',
  description: 'Airport and station pickup points when enabled.',
  flow: 'Hub POI → Zone assignment → Manual booking pickup list',
  examples: 'airports · railway_stations',
};
